// src/features/websites/components/EditorialStyle.tsx
import React from 'react';
import {
  AudioFile as AudioFileIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  CircularProgress,
  Grid,
  IconButton,
  Tooltip,
  Typography
} from '@mui/material';
import AIAssistantIcon from './common/AIAssistantIcon';
import ExpandableTextField from './common/ExpandableTextField';
import CollapsibleSection from '../../../components/common/CollapsibleSection';

interface EditorialStyleSectionProps {
  editorialStyle: string;
  errors: Record<string, string>;
  isLoading: boolean;
  isAiGeneratingEditorialStyle: boolean;
  editorialElapsedTime: number;
  editorialGenerationComplete: boolean;
  onEditorialStyleChange: (value: string) => void;
  onAIButtonClick: () => void;
  onGenerationCompleteClose: () => void;
  icon?: React.ReactNode;
  status?: {
    enabled: boolean;
    state: 'valid' | 'warning' | 'error';
    missingFields?: string[];
  };
}

const EditorialStyleSection: React.FC<EditorialStyleSectionProps> = ({
  editorialStyle,
  errors,
  isLoading,
  isAiGeneratingEditorialStyle,
  editorialElapsedTime,
  editorialGenerationComplete,
  onEditorialStyleChange,
  onAIButtonClick,
  onGenerationCompleteClose,
  icon = <AudioFileIcon sx={{ mr: 1 }} />,
  status,
}) => {
  // Format time as MM:SS
  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  };

  // AI generation button for the section header
  const aiGenerationButton = (
    <Tooltip title="Generate Audio Style with AI">
      <span>
        <IconButton
          color="primary"
          onClick={onAIButtonClick}
          disabled={isLoading || isAiGeneratingEditorialStyle}
          sx={{
            ml: 1,
            '&:hover': {
              backgroundColor: 'action.hover',
            }
          }}
          aria-label="Generate with AI"
        >
          {isAiGeneratingEditorialStyle ? (
            <CircularProgress size={24} color="primary" />
          ) : (
            <AIAssistantIcon />
          )}
        </IconButton>
      </span>
    </Tooltip>
  );

  return (
    <CollapsibleSection
      title="Speech & Audio Style Guide"
      icon={icon}
      actionButton={aiGenerationButton}
      status={status}
    >
      <Typography variant="body2" color="text.secondary" paragraph>
        Describe the speech and audio style and tone of your website. This will guide how content is presented.
      </Typography>

      {isAiGeneratingEditorialStyle && (
        <Alert severity="info" sx={{ mb: 2 }}>
          AI is generating your audio style guide based on analyzing recent articles from your website. This will take a few minutes...
          <Typography variant="body2" sx={{ mt: 1 }}>
            Time elapsed: {formatTime(editorialElapsedTime)}
          </Typography>
        </Alert>
      )}

      {editorialGenerationComplete && !isAiGeneratingEditorialStyle && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Speech & Audio Style Guide generation completed in {formatTime(editorialElapsedTime)}!
          <IconButton
            size="small"
            onClick={onGenerationCompleteClose}
            sx={{ ml: 1 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Alert>
      )}

      <Grid item xs={12}>
        <Box
          sx={{
            border: !editorialStyle || editorialStyle.trim().length === 0 
              ? '2px solid' 
              : 'none',
            borderColor: 'warning.main',
            borderRadius: 1,
            p: !editorialStyle || editorialStyle.trim().length === 0 ? 2 : 0,
          }}
        >
          <ExpandableTextField
            fullWidth
            label="Speech & Audio Style Guide"
            value={editorialStyle}
            onChange={(e) => onEditorialStyleChange(e.target.value)}
            error={!!errors['demographics']}
            helperText={errors['demographics'] || (!editorialStyle || editorialStyle.trim().length === 0 ? 'This important field should be filled in for best results' : '')}
            disabled={isLoading || isAiGeneratingEditorialStyle}
            defaultRows={3}
            expandedRows={25}
            placeholder="Describe your voice, tone, and style guidelines"
          />
        </Box>
      </Grid>
    </CollapsibleSection>
  );
};

export default EditorialStyleSection;